import { Item } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  A3InnuvaEducationLevelDocument,
  A3innuvaEducationLevelFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { SpanishContractData } from 'modules/contracts/hooks/useFetchSpanishContractData'
import { translate } from 'modules/contracts/lib/translate'
import {
  educationLevelsToOptions,
  fullName,
} from 'modules/payroll_integrations_a3innuva/lib/a3innuvaHelpers'

interface Props {
  form: Form
  translations: { [key: string]: string }
  spanishContractData: SpanishContractData
}

const EducationLevel = ({ form, translations, spanishContractData }: Props) => {
  const session = useCurrentSession()
  const educationLevelName = form.get('es_education_level_payroll_integration_name').value
  const educationLevelCode = form.get('es_education_level_payroll_integration_code').value
  const educationLevelId = form.get('es_education_level_id').value
  const spainLevel = educationLevelId
    ? spanishContractData.educationLevels.find(({ id }) => id === Number(educationLevelId))
    : null

  const setCurrentEducationLevel = (a3innuvaEducationLevels: A3innuvaEducationLevelFragment[]) => {
    if (spainLevel) {
      const currentLevel = a3innuvaEducationLevels.find(({ name }) => name === spainLevel?.name)

      if (currentLevel) {
        form.setValues({
          es_education_level_payroll_integration_code: currentLevel.code,
          es_education_level_payroll_integration_name: currentLevel.name,
        })
      }
    }
  }

  const isFormInitialized = () => {
    if (!form.has('es_education_level_payroll_integration_name')) return false
    return form.get('es_education_level_payroll_integration_name').value === spainLevel?.name
  }

  const { data: educationLevelStepData, loading } = useQuery(A3InnuvaEducationLevelDocument, {
    variables: {
      companyId: session.company.id,
      currentUserId: session.access.userId,
    },
  })

  const a3innuvaEducationLevels =
    educationLevelStepData?.payrollIntegrationsA3innuva.educationLevels ?? []

  if (!loading && !isFormInitialized()) {
    setCurrentEducationLevel(a3innuvaEducationLevels)
  }

  const options = educationLevelsToOptions(a3innuvaEducationLevels)

  const onEducationLevelChanged = (levelCode: Item) => {
    const selectedEducationLevel = a3innuvaEducationLevels.find(
      ({ code }) => code === levelCode.value
    )
    form.setValues({
      es_education_level_payroll_integration_name: selectedEducationLevel?.name,
      es_education_level_payroll_integration_code: selectedEducationLevel?.code,
      es_education_level_id: undefined,
    })
  }

  const labelNotFound = () => {
    return options.length
      ? i18n.t(
          'contract_versions.forms.contract_extra_information.a3innuva.education_level_not_found'
        )
      : i18n.t('contract_versions.forms.contract_extra_information.a3innuva.label_not_found')
  }

  return (
    <DropdownSearchBar
      name={fullName(educationLevelCode, educationLevelName)}
      label={translate(translations, 'education_level') || ''}
      onSelect={onEducationLevelChanged}
      labelNotFound={labelNotFound()}
      data={options}
      searchLabel={i18n.t(
        'contract_versions.forms.contract_extra_information.a3innuva.search_label'
      )}
      loading={loading}
      popoverWidth="s400"
      ellipsis={false}
    />
  )
}

export default observer(EducationLevel)
